import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { TailSpin } from "react-loader-spinner";

import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs/tabs.tsx";
import { Select } from "../../components/ui/select/select.tsx";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import { useHolidays } from "../../hooks/use-holidays.ts";
import { REGION_TABS, REGION_LABELS } from "../../components/features/holidays/constants.ts";
import type { HolidayRegion } from "../../components/features/holidays/types.ts";
import { HolidaysHero } from "../../components/features/holidays/components/holidays-hero/holidays-hero.tsx";
import { HolidaysStats } from "../../components/features/holidays/components/holidays-stats/holidays-stats.tsx";
import { HolidaysTable } from "../../components/features/holidays/components/holidays-table/holidays-table.tsx";
import { generateYearAll } from "../../../utils/helpers.js";
import { useSqeLabs } from "../../hooks/use-sqe-labs.ts";

import styles from "./holidays.module.css";

const SQE_REGION: HolidayRegion = "SQE";

function parseHolidayDateMs(dateStr: string): number {
  const parsed = Date.parse(dateStr);
  if (Number.isFinite(parsed)) return parsed;
  const date = new Date(dateStr);
  return Number.isFinite(date.getTime()) ? date.getTime() : 0;
}

function filterByTab(rows: import("../../components/features/holidays/types.ts").Holiday[], tab: HolidayRegion) {
  return rows
    .filter((h) => h.region === tab)
    .sort((a, b) => parseHolidayDateMs(a.date) - parseHolidayDateMs(b.date));
}

function filterForSQELabs(rows: import("../../components/features/holidays/types.ts").Holiday[]) {
  return rows
    .filter((h) => h.region === SQE_REGION)
    .sort((a, b) => parseHolidayDateMs(a.date) - parseHolidayDateMs(b.date));
}

export default function HolidaysPage() {
  const isSQEEmployee = useSqeLabs();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [regionTab, setRegionTab] = useState<HolidayRegion>(() =>
    isSQEEmployee
      ? SQE_REGION
      : "GLOBAL",
  );
  const { holidays, loading, error } = useHolidays(year);
  const isSQERestrictedView = isSQEEmployee;
  const visibleRegionTabs = isSQERestrictedView
    ? REGION_TABS.filter((tab) => tab.id === SQE_REGION)
    : REGION_TABS;

  const yearOptions = useMemo(
    () =>
      generateYearAll(2024).map((y: number) => ({
        value: String(y),
        label: String(y),
      })),
    [],
  );

  const filteredRows = useMemo(
    () =>
      isSQERestrictedView
        ? filterForSQELabs(holidays)
        : filterByTab(holidays, regionTab),
    [holidays, regionTab, isSQERestrictedView],
  );

  const emptyMessage = `No holidays for ${year} in ${REGION_LABELS[regionTab]}.`;

  useEffect(() => {
    if (isSQERestrictedView) {
      setRegionTab(SQE_REGION);
    }
  }, [isSQERestrictedView]);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Home", path: "/dashboard" },
      { label: "Holidays" },
    ]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);

  return (
    <PageShell>
      {loading && (
        <div className="overlay">
          <div className="loader-container">
            <TailSpin
              height="100"
              width="100"
              radius="1"
              color="blue"
              ariaLabel="loading-holidays"
              visible
            />
          </div>
        </div>
      )}


      <HolidaysHero />
      {!isSQERestrictedView && <HolidaysStats holidays={holidays} />}

      {error ? (
        <p className={clsx("body-sm", styles.error)} role="alert">
          {error}
        </p>
      ) : null}

      <Card className={styles.tableCard} padding="5xl">
        <div className={styles.tableCardHeader}>
          <div className={styles.headerRow}>
            <h2 className={clsx("heading-sm", styles.tableTitle)}>
              Upcoming Holidays
            </h2>
            <div className={styles.yearSlot}>
              <Select
                id="holidays-year"
                placeholder="Year"
                value={String(year)}
                onChange={(v) => setYear(Number(v))}
                options={yearOptions}
              />
            </div>
          </div>
        </div>

        <Tabs value={regionTab} onValueChange={(v) => setRegionTab(v as HolidayRegion)}>
          <TabsList className={styles.tabsList}>
            {visibleRegionTabs.map((tab) => (
              <TabsTrigger key={tab.id} value={tab.id} className={styles.tabTrigger}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={regionTab} className={styles.tabPanel}>
            <HolidaysTable rows={filteredRows} emptyMessage={emptyMessage} />
          </TabsContent>
        </Tabs>
      </Card>
    </PageShell>
  );
}
